import { useMemo } from 'react'
import { resolveCourseCardPresentation } from '#app/components/entities/courses/cardPresentation/cardPresentation'
import { CourseCardBadge } from '#app/components/entities/courses/public/CourseCardBadge'
import { CourseCardShell } from '#app/components/entities/courses/public/CourseCardShell'
import { cn } from '#app/utils/misc'
import { applyMainEntityToPath } from '#app/utils/pz/brand/mainEntity'
import { publicRoutes } from '#app/utils/pz/routes'
import { ResponsiveMarketingImage } from './ResponsiveMarketingImage'

/**
 * CoursesGrid - Display a grid of courses
 *
 * This component renders a grid of course cards with:
 * - Title and description
 * - Optional cover image
 * - Category badge
 * - Optional "View All" button
 *
 * Based on the GitHub projects showcase pattern
 */

export interface CourseItem {
	title: string
	description?: string
	thumbnail?: string
	category?: string
	parentCategory?: string
	link?: string
	metadata?: string // e.g., number of lessons, duration, etc.
	moduleCount?: number
	topicCount?: number
}

export interface PersonalMainEntityItem {
	title: string
	description?: string
	metadata?: string
	badgeLabel?: string
	link?: string
}

export interface CoursesGridProps {
	title?: string
	subtitle?: string
	columns?: 1 | 2 | 3 | 4
	courses?: CourseItem[]
	personalMainEntities?: PersonalMainEntityItem[]
	personalMainEntityCardImage?: string
	showViewAllButton?: boolean
	viewAllButtonText?: string
	viewAllButtonLink?: string
}

const columnClasses = {
	1: 'md:grid-cols-1',
	2: 'md:grid-cols-2',
	3: 'md:grid-cols-2 lg:grid-cols-3',
	4: 'md:grid-cols-2 lg:grid-cols-4',
}

const MAX_VISIBLE_COURSES = 12

export function CoursesGrid({
	title = 'Explore Courses',
	subtitle,
	columns = 4,
	courses = [],
	personalMainEntities = [],
	personalMainEntityCardImage,
	showViewAllButton = false,
	viewAllButtonText = 'View All Courses',
	viewAllButtonLink = publicRoutes.courses.list(),
}: CoursesGridProps) {
	const normalizeHref = (href: string) => {
		if (!href) return href
		if (/^https?:\/\//i.test(href)) return href
		return applyMainEntityToPath(href)
	}

	const totalSlots = MAX_VISIBLE_COURSES
	const personalEntitiesToShow = personalMainEntities.slice(0, totalSlots)
	const remainingSlots = Math.max(totalSlots - personalEntitiesToShow.length, 0)

	const displayCourses = useMemo(
		() => (courses ?? []).slice(0, remainingSlots),
		[courses, remainingSlots],
	)

	const interleavedItems = useMemo(() => {
		const total = personalEntitiesToShow.length + displayCourses.length
		if (total === 0)
			return [] as Array<
				| { kind: 'personal'; item: PersonalMainEntityItem; key: string }
				| { kind: 'catalog'; item: CourseItem; key: string }
			>

		const personalSlotIndices = new Set<number>()
		const personalCount = personalEntitiesToShow.length
		if (personalCount > 0) {
			for (let i = 0; i < personalCount; i++) {
				const slot = Math.round(((i + 1 / 3) * total) / personalCount)
				personalSlotIndices.add(Math.min(Math.max(slot, 0), total - 1))
			}
		}

		const result: Array<
			| { kind: 'personal'; item: PersonalMainEntityItem; key: string }
			| { kind: 'catalog'; item: CourseItem; key: string }
		> = []
		let personalIdx = 0
		let catalogIdx = 0
		for (let slot = 0; slot < total; slot++) {
			const wantsPersonal =
				personalSlotIndices.has(slot) && personalIdx < personalCount
			if (wantsPersonal) {
				const item = personalEntitiesToShow[personalIdx]!
				result.push({
					kind: 'personal',
					item,
					key: item.link ?? `personal-${personalIdx}-${item.title}`,
				})
				personalIdx++
			} else if (catalogIdx < displayCourses.length) {
				const item = displayCourses[catalogIdx]!
				result.push({
					kind: 'catalog',
					item,
					key: item.link ?? `catalog-${catalogIdx}-${item.title}`,
				})
				catalogIdx++
			} else if (personalIdx < personalCount) {
				const item = personalEntitiesToShow[personalIdx]!
				result.push({
					kind: 'personal',
					item,
					key: item.link ?? `personal-${personalIdx}-${item.title}`,
				})
				personalIdx++
			}
		}
		return result
	}, [personalEntitiesToShow, displayCourses])

	return (
		<section className="mx-auto max-w-7xl py-16 md:py-24">
			<div className="container px-4 md:px-6">
				{/* Header */}
				<div className="mb-10 text-center">
					{title && <h2 className="text-title-2">{title}</h2>}
					{subtitle && (
						<p className="text-muted-foreground mx-auto mt-4 max-w-4xl text-lg font-semibold">
							{subtitle}
						</p>
					)}
				</div>

				<div
					className={cn(
						'mt-10 grid grid-cols-1 gap-8',
						columnClasses[columns] || columnClasses[3],
					)}
				>
					{interleavedItems.map((entry) => {
						if (entry.kind === 'personal') {
							return (
								<PersonalMainEntityCard
									key={entry.key}
									entity={entry.item}
									cardImage={personalMainEntityCardImage}
									normalizeHref={normalizeHref}
								/>
							)
						}
						const course = entry.item
						const descriptionText = course.description?.trim()
						const presentation = resolveCourseCardPresentation(
							{
								kind: 'course',
								primarySubcategoryLabel: course.category ?? null,
								moduleCount: course.moduleCount,
								topicCount: course.topicCount,
							},
							'home',
							() => '',
						)
						return (
							<CourseCardShell
								key={entry.key}
								className="hover:shadow-md"
								media={
									course.thumbnail ? (
										<a
											href={course.link ? normalizeHref(course.link) : '#'}
											className="block h-full w-full"
										>
											<ResponsiveMarketingImage
												src={course.thumbnail}
												alt={course.title}
												className="h-full w-full object-cover"
												width={960}
												height={720}
												sizes="(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw"
											/>
										</a>
									) : undefined
								}
								badges={
									presentation.badges[0] ? (
										<CourseCardBadge label={presentation.badges[0].label} />
									) : undefined
								}
								title={
									<a
										href={course.link ? normalizeHref(course.link) : '#'}
										className="group-hover:text-primary line-clamp-2 text-lg font-semibold"
									>
										{course.title}
									</a>
								}
								description={descriptionText}
							/>
						)
					})}
				</div>

				{/* View All Button */}
				{showViewAllButton && (
					<div className="mt-10 flex justify-center">
						<a
							href={normalizeHref(viewAllButtonLink)}
							className="border-border bg-background hover:bg-accent hover:text-accent-foreground inline-flex h-9 items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm font-medium whitespace-nowrap shadow-xs transition-all"
						>
							{viewAllButtonText}
						</a>
					</div>
				)}
			</div>
		</section>
	)
}

function PersonalMainEntityCard({
	entity,
	cardImage,
	normalizeHref,
}: {
	entity: PersonalMainEntityItem
	cardImage?: string
	normalizeHref: (href: string) => string
}) {
	const href = entity.link ? normalizeHref(entity.link) : undefined
	const badgeLabel = entity.badgeLabel ?? 'Ruta propia'

	const mediaInner = cardImage ? (
		<>
			<ResponsiveMarketingImage
				src={cardImage}
				alt=""
				className="h-full w-full object-cover transition-transform duration-500 group-hover/media:scale-[1.02]"
				width={800}
				height={1000}
				sizes="(min-width: 1024px) 33vw, 100vw"
			/>
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0"
				style={{
					background:
						'linear-gradient(135deg, rgba(13,17,23,0.05) 0%, rgba(13,17,23,0.35) 100%)',
				}}
			/>
		</>
	) : null

	return (
		<CourseCardShell
			className="hover:shadow-md"
			media={
				cardImage ? (
					href ? (
						<a href={href} className="group/media relative block h-full w-full">
							{mediaInner}
						</a>
					) : (
						<div className="group/media relative block h-full w-full">
							{mediaInner}
						</div>
					)
				) : undefined
			}
			badges={
				<CourseCardBadge
					label={badgeLabel}
					className="border-primary/25 bg-primary/8 text-primary"
				/>
			}
			title={
				href ? (
					<a
						href={href}
						className="group-hover:text-primary line-clamp-2 text-lg font-semibold"
					>
						{entity.title}
					</a>
				) : (
					<span className="line-clamp-2 block text-lg font-semibold">
						{entity.title}
					</span>
				)
			}
			description={entity.description}
		/>
	)
}
